import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useLanguage } from "../Translations/LenguageContext";
import FormatDateTime from "../Functions/FormatDate";
import ErrorMessage from "../Components/ErrorMessage";
import SourceOverviewLoader from "../Loaders/SourceOverviewLoader";
import { TbReload } from "react-icons/tb";
import Spinner from "../Components/Spinner";

const GEN_BACKEND_LINK = process.env.REACT_APP_GEN_BACKEND_LINK;

const OverviewSourceModal = ({
    account_id,
    context_id,
    org_id,
    selectedSource,
    show,
    handleClose,
    openContextModal,
    setOpenCustomModal
}) => {
    const { t } = useLanguage();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [totalePages, setTotalPages] = useState(null)
    const [summarizedPages, setSummarizedPages] = useState(null)
    const [totalChunks, setTotalChunks] = useState(null)
    const [lastUpdated, setLastUpdated] = useState(null)
    const [pageSummaries, setPageSummaries] = useState([])
    const [openSummary, setOpenSummary] = useState(null)
    const [pageId, setPageId] = useState(null)

    const [errorMessage, setErrorMessage] = useState({
        message: "",
        display: false
    });

    const fetchData = async () => {
        setLoading(true);
        setErrorMessage({ message: "", display: false });
        setData(null);

        try {
            const response = await fetch(
                `${GEN_BACKEND_LINK}/get_context_source_overview`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        account_id,
                        context_id,
                        source_id: selectedSource?.id
                    }),
                }
            );

            const result = await response.json();

            if (!response.ok) {
                throw new Error(
                    result.error || "Failed to fetch source overview."
                );
            }

            setData(result.data || {});

            setTotalPages(result.data.total_pages)
            setSummarizedPages(result.data.summarized_pages)
            setTotalChunks(result.data.total_chunks)
            setLastUpdated(result.data.last_updated)
            setPageSummaries(result.data.page_summaries)
        } catch (err) {
            if (err.name === "AbortError") return;

            console.error("Error fetching source overview:", err);

            setErrorMessage({
                message: err.message || "Failed to load source overview.",
                display: true
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadDocument = async (e, page) => {
        e.preventDefault();

        try {
            const response = await fetch(
                `${GEN_BACKEND_LINK}/download_document`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        account_id,
                        org_id,
                        context_id,
                        file_id: page.url
                    }),
                }
            );

            if (!response.ok) {
                const result = await response.json().catch(() => ({}));
                throw new Error(
                    result.error || "Failed to download document."
                );
            }

            const blob = await response.blob();

            const disposition = response.headers.get("Content-Disposition");
            const match = disposition?.match(
                /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/
            );

            const filename = match
                ? decodeURIComponent(match[1] || match[2])
                : page.title || "document";

            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");

            link.href = downloadUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();

            window.URL.revokeObjectURL(downloadUrl);
        } catch (err) {
            console.error("Error downloading document:", err);

            setErrorMessage({
                message: err.message || "Failed to download document.",
                display: true,
            });
        }
    };

    useEffect(() => {
        if (!show || !selectedSource?.id) return;
        fetchData();

    }, [show, account_id, context_id, selectedSource?.id]);

    const closeModal = () => {
        handleClose();
        setLoading(false)
        setErrorMessage({ message: "", display: false });
        setData(null)
        setTotalPages(null)
        setSummarizedPages(null)
        setTotalChunks(null)
        setLastUpdated(null)
        setPageSummaries([])
        setOpenSummary(null)
        setPageId(null)
        openContextModal()
    };

    const toggleSummary = (page_id) => {
        if (pageId === page_id) {
            setOpenSummary(false);
            setPageId(null);
        } else {
            setPageId(page_id);
            setOpenSummary(true);
        }
    };

    const openCustomM = () => {
        setOpenCustomModal(true)
        handleClose()
    }

    return (
        <Modal show={show} centered onHide={closeModal}>
            <div className="modal_header">
                <p className="modal_header_title">
                    {t.context.source_overview}
                </p>
                <button className="context_window_footer_btn" onClick={fetchData}>
                    {loading ? (
                        <Spinner loading={loading} />
                    ) : (
                        <TbReload />
                    )}
                </button>
            </div>
            <div className="modal_main_cont">
                {loading ? (
                    <SourceOverviewLoader />
                ) : !data ? (
                    <div className="organizations_cont_no_items_mex fade-in">
                        <p>{t.no_data_found}</p>
                    </div>
                ) : (
                    <>
                        <div className="overview_header">
                            <div className="overview_row">
                                <div className="overview_item fade-in">
                                    <div className="overview_item_inner">
                                        <p className="overview_item_header">
                                            {t.context.total_pages}
                                        </p>
                                        <p className="overview_item_body">
                                            {totalePages}
                                        </p>
                                    </div>
                                </div>

                                <div className="overview_item fade-in">
                                    <div className="overview_item_inner">
                                        <p className="overview_item_header">
                                            {t.context.summarized_pages}
                                        </p>
                                        <p className="overview_item_body">
                                            {summarizedPages}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="overview_row">
                                <div className="overview_item fade-in">
                                    <div className="overview_item_inner">
                                        <p className="overview_item_header">
                                            {t.context.total_chunks}
                                        </p>
                                        <p className="overview_item_body">
                                            {totalChunks}
                                        </p>
                                    </div>
                                </div>

                                <div className="overview_item fade-in">
                                    <div className="overview_item_inner">
                                        <p className="overview_item_header">
                                            {t.context.last_updated}
                                        </p>
                                        <p className="overview_item_body">
                                            {FormatDateTime(lastUpdated)}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {pageSummaries.length > 0 ? (
                            <div className="summary_context_source_cont">
                                <p className="summary_context_source_title fade-in">
                                    {pageSummaries.length == 1 ? t.context.one_page_summary : `${t.context.page_summaries} (${pageSummaries.length})`}
                                </p>

                                {pageSummaries.map((page, index) => (
                                    <div
                                        className={`overview_list_item fade-in ${pageId == page.id && "overview_list_item_open"}`}
                                        key={page.id}
                                    >
                                        <div className="summary_header">
                                            <div className="summary_header_title_cont" title={page.title}>
                                                <span className="summary_header_title">{page.title}</span>
                                                <button
                                                    className="summary_header_btn"
                                                    onClick={() => toggleSummary(page.id)}
                                                >
                                                    <i className={`fa-solid fa-chevron-${pageId == page.id ? "up" : "down"}`}></i>
                                                </button>
                                            </div>
                                        </div>

                                        <p className="summary_context_source">
                                            {page.summary || t.context.no_summary_available}
                                        </p>

                                        <div className="summary_context_footer">
                                            {page.indexed_at && (
                                                <div className="summary_context_footer_items">
                                                    <span>{t.context.indexed_at}</span>
                                                    <p className="summary_context_footer_items_p" title={FormatDateTime(page.indexed_at)}>{FormatDateTime(page.indexed_at)}</p>
                                                </div>
                                            )}
                                            {page.chunk_count && (
                                                <div className="summary_context_footer_items">
                                                    <span>{t.context.chunks}</span>
                                                    <p className="summary_context_footer_items_p" title={page.chunk_count}>{page.chunk_count}</p>
                                                </div>
                                            )}
                                            {page.url && (
                                                <div className="summary_context_footer_items">
                                                    <span>
                                                        {selectedSource?.source_type === "website"
                                                            ? "URL"
                                                            : selectedSource?.source_type === "documents"
                                                                ? "File"
                                                                : t.context.custom_source}
                                                    </span>

                                                    {selectedSource?.source_type === "documents" ? (
                                                        <a
                                                            href="#"
                                                            title={page.title}
                                                            onClick={(e) => handleDownloadDocument(e, page)}
                                                            className="summary_context_footer_items_p"
                                                        >
                                                            {page.title || page.url}
                                                        </a>
                                                    ) : selectedSource?.source_type === "website" ? (
                                                        <a
                                                            title={page.url}
                                                            href={page.url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="summary_context_footer_items_p"
                                                        >
                                                            {page.url}
                                                        </a>
                                                    ):(
                                                        <button className="see_source_btn" onClick={openCustomM}>
                                                            {t.context.read} <i className="fa-solid fa-chevron-right"></i>
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="organizations_cont_no_items_mex fade-in">
                                <p>{t.no_data_found}</p>
                            </div>
                        )}
                    </>
                )}
            </div>

            <div className="modal_footer">
                <div className="modal_footer_err_cont">
                    <ErrorMessage err={errorMessage} />
                </div>

                <div className="modal_footer_btn_cont">
                    <button
                        className="modale_close_btn"
                        onClick={closeModal}
                    >
                        <i className="fa-solid fa-chevron-left"></i> {t.back}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default OverviewSourceModal;